import { z } from "zod";
import { CLIENT_PORTAL_FORBIDDEN_COPY_TERMS, FORBIDDEN_INTERNAL_ID_TERMS, } from "../client-safe/forbidden-leak-registry.js";
import { assertNoForbiddenClientSafePhrases } from "../reports/sanitize-client-safe-report.js";
export const PROJECT_RECORD_CENTER_FORBIDDEN_JSON_KEYS = [
    "reportId",
    "fixKitId",
    "salesRoomId",
    "snapshotId",
    "afterFixProofId",
    "runId",
    "tenantId",
    "agencyId",
    "projectId",
    "traceId",
    "checkId",
    "artifactSetHash",
    "clientLinkId",
    "ownershipRecordId",
    "findingId",
    "sourceRef",
    "sourceFindingId",
    "businessGraphRevisionId",
    "qualityGateResultId",
    "qualityGateResultRef",
    "normalizedUrl",
    "internalReportJson",
    "htmlByteLength",
    "sourceExternalReadinessReportId",
    "publicReportRef",
];
const PROJECT_RECORD_CENTER_FORBIDDEN_PATTERNS = [
    /agency_[a-z0-9_]+/i,
    /project_[a-z0-9_]+/i,
    /rpt_ext_[a-z0-9_]+/i,
    /fkit_ext_[a-z0-9_]+/i,
    /snapshot_[a-z0-9_]+/i,
    /scan_[a-z0-9_]+/i,
    /imdrft_[a-z0-9_]+/i,
    /draft_[a-z0-9_]+/i,
    /erfind_[a-z0-9_]+/i,
    /cl_[a-z0-9_]+/i,
    /traceId/i,
    /worker/i,
    /artifactSetHash/i,
    /qualityGate/i,
    /BusinessGraph/i,
    /\/v1\//i,
];
const CLIENT_STATUS_LABELS = {
    awaiting_site_attachment: "Waiting for site link",
    awaiting_scan: "Waiting for scan",
    awaiting_readiness: "Waiting for readiness review",
    awaiting_fix_kit: "Waiting for fix plan",
    awaiting_sales_room: "Waiting for client review package",
    change_requested: "Change requested",
    awaiting_client_approval: "Waiting for your approval",
    client_approved: "Approved — follow-up proof optional",
    after_fix_pending: "After-fix proof in progress",
    awaiting_handoff: "Waiting for ownership handoff",
    record_complete: "Delivery record complete",
};
const PROJECT_MODE_LABELS = {
    EXTERNAL_PROOF_ONLY: "External proof review",
    DEFAULT_GENERATION: "Website generation",
};
const DOCUMENT_LIFECYCLE_LABELS = {
    READY_CLIENT_SAFE: "Ready",
    READY_CLIENT_SAFE_SUMMARY: "Ready",
    READY_INTERNAL: "In progress",
    missing: "Not available",
    pending: "Pending",
    recorded: "Recorded",
};
const SanitizedReadinessSectionV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedReadiness.v1"),
    headline: z.string().min(1).max(200),
    findings: z.array(z
        .object({
        categoryLabel: z.string().min(1).max(100),
        severity: z.string().min(1).max(50),
        summary: z.string().min(1).max(500),
        recommendation: z.string().min(1).max(2000),
    })
        .strict()),
    whatWeDoNotPromise: z.array(z.string().min(1).max(500)),
    generatedAt: z.string().datetime(),
})
    .strict();
const SanitizedFixKitSectionV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedFixKit.v1"),
    executiveSummary: z.string().min(1).max(2000),
    actionItems: z.array(z
        .object({
        issueSummary: z.string().min(1).max(500),
        severity: z.string().min(1).max(50),
        priority: z.string().min(1).max(10),
        recommendedFix: z.string().min(1).max(2000),
        clientSafeExplanation: z.string().min(1).max(2000),
    })
        .strict()),
    whatWeDoNotPromise: z.array(z.string().min(1).max(500)),
    beforeAfterRelaunchAngle: z.string().min(1).max(2000),
    generatedAt: z.string().datetime(),
})
    .strict();
const SanitizedSalesRoomSectionV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedSalesRoom.v1"),
    approvalState: z.string().min(1).max(100),
    siteSummary: z
        .object({
        headline: z.string().min(1).max(200),
        scanStatusLabel: z.string().min(1).max(100),
        checkHighlights: z.array(z.string().min(1).max(500)).optional(),
    })
        .strict()
        .optional(),
    readinessHeadline: z.string().min(1).max(200).optional(),
    readinessFindings: z
        .array(z
        .object({
        categoryLabel: z.string().min(1).max(100),
        summary: z.string().min(1).max(500),
    })
        .strict())
        .optional(),
    topIssues: z
        .array(z
        .object({
        severity: z.string().min(1).max(50),
        summary: z.string().min(1).max(500),
        recommendation: z.string().min(1).max(2000),
    })
        .strict())
        .optional(),
    fixKitExecutiveSummary: z.string().min(1).max(2000).optional(),
    fixKitItems: z
        .array(z
        .object({
        issueSummary: z.string().min(1).max(500),
        severity: z.string().min(1).max(50),
        recommendedFix: z.string().min(1).max(2000),
        clientSafeExplanation: z.string().min(1).max(2000),
    })
        .strict())
        .optional(),
    agencyRecommendation: z.string().min(1).max(2000).optional(),
    clientRequestedItems: z
        .array(z
        .object({
        label: z.string().min(1).max(200),
        detail: z.string().min(1).max(2000),
    })
        .strict())
        .optional(),
    blockedItems: z
        .array(z
        .object({
        label: z.string().min(1).max(200),
        detail: z.string().min(1).max(2000),
    })
        .strict())
        .optional(),
    handoffPlaceholder: z
        .object({
        summary: z.string().min(1).max(2000),
        statusLabel: z.string().min(1).max(100),
    })
        .strict()
        .optional(),
    whatWeDoNotPromise: z.array(z.string().min(1).max(500)),
    generatedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
const SanitizedAfterFixSectionV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedAfterFix.v1"),
    headline: z.string().min(1).max(200),
    summaryCounts: z
        .object({
        fixed: z.number().int().min(0),
        stillOpen: z.number().int().min(0),
        needsProof: z.number().int().min(0),
    })
        .strict(),
    items: z.array(z
        .object({
        label: z.string().min(1).max(500),
        status: z.string().min(1).max(100),
        summary: z.string().min(1).max(2000),
    })
        .strict()),
    whatWeDoNotPromise: z.array(z.string().min(1).max(500)),
    comparedAt: z.string().datetime(),
    generatedAt: z.string().datetime(),
})
    .strict();
const SanitizedOwnershipSectionV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedOwnership.v1"),
    headline: z.string().min(1).max(200),
    subcopy: z.string().min(1).max(2000),
    checklist: z.array(z
        .object({
        label: z.string().min(1).max(200),
        stateLabel: z.string().min(1).max(100),
        note: z.string().min(1).max(500).nullable(),
    })
        .strict()),
    riskScoreLabel: z.string().min(1).max(50),
    riskScoreExplanation: z.string().min(1).max(2000),
    disclaimers: z.array(z.string().min(1).max(500)),
    updatedAt: z.string().datetime(),
})
    .strict();
export const SanitizedProjectRecordCenterClientSectionsV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedClientSections.v1"),
    readiness: SanitizedReadinessSectionV1Schema.optional(),
    fixKit: SanitizedFixKitSectionV1Schema.optional(),
    salesRoom: SanitizedSalesRoomSectionV1Schema.optional(),
    afterFix: SanitizedAfterFixSectionV1Schema.optional(),
    ownership: SanitizedOwnershipSectionV1Schema.optional(),
})
    .strict();
export const SanitizedProjectRecordCenterClientViewV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.sanitizedClientView.v1"),
    viewVersion: z.literal("0.1"),
    summary: z
        .object({
        schemaId: z.literal("projectRecordCenter.sanitizedClientSummary.v1"),
        projectModeLabel: z.string().min(1).max(100),
        currentStatusLabel: z.string().min(1).max(100),
        nextAction: z.string().min(1).max(500).optional(),
        clientSafe: z.literal(true),
        generatedAt: z.string().datetime(),
    })
        .strict(),
    timeline: z.array(z
        .object({
        schemaId: z.literal("projectRecordCenter.timelineEntry.v1"),
        entryId: z.string().min(1),
        occurredAt: z.string().datetime(),
        phase: z.string().min(1).max(100),
        headline: z.string().min(1).max(200),
        summary: z.string().min(1).max(2000),
        status: z.enum(["complete", "pending", "blocked", "skipped"]),
    })
        .strict()),
    documents: z.array(z
        .object({
        schemaId: z.literal("projectRecordCenter.document.v1"),
        docType: z.enum(["readiness", "fix_kit", "sales_room", "after_fix", "ownership"]),
        label: z.string().min(1).max(200),
        lifecycleStatusLabel: z.string().min(1).max(100),
        clientSafeRef: z.string().min(1).max(200).optional(),
        available: z.boolean(),
    })
        .strict()),
    handoff: z
        .object({
        schemaId: z.literal("projectRecordCenter.handoffSummary.v1"),
        ownershipStatus: z.string().min(1).max(200),
        clientSummary: z.string().min(1).max(2000),
        handoffAvailable: z.boolean(),
    })
        .strict()
        .optional(),
    warnings: z
        .array(z
        .object({
        schemaId: z.literal("projectRecordCenter.warning.v1"),
        code: z.string().min(1).max(100),
        message: z.string().min(1).max(500),
        severity: z.enum(["info", "warning"]),
    })
        .strict())
        .optional(),
    sections: SanitizedProjectRecordCenterClientSectionsV1Schema.optional(),
})
    .strict();
function clientStatusLabel(status) {
    return CLIENT_STATUS_LABELS[status] ?? "In progress";
}
function projectModeLabel(mode) {
    return PROJECT_MODE_LABELS[mode] ?? "Project review";
}
function documentLifecycleLabel(status) {
    return DOCUMENT_LIFECYCLE_LABELS[status] ?? "In progress";
}
function sanitizeTimelineEntry(entry) {
    let summary = entry.summary;
    if (entry.phase === "intake") {
        summary = "Review started for your submitted website.";
    }
    else if (entry.phase === "scan") {
        summary = entry.status === "complete" ? "External scan completed." : "External scan in progress.";
    }
    else if (entry.phase === "import") {
        summary =
            entry.status === "complete"
                ? "Import suggestions were reviewed."
                : "Import suggestions are being reviewed.";
    }
    else if (entry.phase === "confirmation") {
        summary = "Business profile details were confirmed.";
    }
    else if (summary.includes("http://") || summary.includes("https://")) {
        summary = entry.headline;
    }
    if (/Scan status:\s*[A-Z_]+/.test(summary)) {
        summary = entry.status === "complete" ? "External scan completed." : "External scan in progress.";
    }
    if (/Import review status:\s*[A-Z_]+/.test(summary)) {
        summary =
            entry.status === "complete"
                ? "Import suggestions were reviewed."
                : "Import suggestions are being reviewed.";
    }
    if (/After-fix proof status:\s*[A-Z_]+/.test(summary)) {
        summary = "After-fix proof is being prepared.";
    }
    return {
        ...entry,
        summary,
    };
}
function sanitizeReadinessSection(section) {
    return {
        schemaId: "projectRecordCenter.sanitizedReadiness.v1",
        headline: section.headline,
        findings: section.findings.map((finding) => ({
            categoryLabel: finding.categoryLabel,
            severity: finding.severity,
            summary: finding.summary,
            recommendation: finding.recommendation,
        })),
        whatWeDoNotPromise: [...section.whatWeDoNotPromise],
        generatedAt: section.generatedAt,
    };
}
function sanitizeFixKitSection(section) {
    return {
        schemaId: "projectRecordCenter.sanitizedFixKit.v1",
        executiveSummary: section.executiveSummary,
        actionItems: section.actionItems.map((item) => ({
            issueSummary: item.issueSummary,
            severity: item.severity,
            priority: item.priority,
            recommendedFix: item.recommendedFix,
            clientSafeExplanation: item.clientSafeExplanation,
        })),
        whatWeDoNotPromise: [...section.whatWeDoNotPromise],
        beforeAfterRelaunchAngle: section.beforeAfterRelaunchAngle,
        generatedAt: section.generatedAt,
    };
}
function scanStatusLabel(status) {
    switch (status) {
        case "COMPLETE":
            return "Scan complete";
        case "PROCESSING":
            return "Scan in progress";
        case "FAILED":
            return "Scan failed";
        case "PENDING":
            return "Scan pending";
        default:
            return "Scan status recorded";
    }
}
function sanitizeSalesRoomSection(section) {
    return {
        schemaId: "projectRecordCenter.sanitizedSalesRoom.v1",
        approvalState: section.approvalState,
        siteSummary: section.siteSummary
            ? {
                headline: section.siteSummary.headline,
                scanStatusLabel: scanStatusLabel(section.siteSummary.scanStatus),
                checkHighlights: section.siteSummary.checkHighlights
                    ? [...section.siteSummary.checkHighlights]
                    : undefined,
            }
            : undefined,
        readinessHeadline: section.readinessHeadline,
        readinessFindings: section.readinessFindings?.map((finding) => ({
            categoryLabel: finding.categoryLabel,
            summary: finding.summary,
        })),
        topIssues: section.topIssues?.map((issue) => ({
            severity: issue.severity,
            summary: issue.summary,
            recommendation: issue.recommendation,
        })),
        fixKitExecutiveSummary: section.fixKitExecutiveSummary,
        fixKitItems: section.fixKitItems?.map((item) => ({
            issueSummary: item.issueSummary,
            severity: item.severity,
            recommendedFix: item.recommendedFix,
            clientSafeExplanation: item.clientSafeExplanation,
        })),
        agencyRecommendation: section.agencyRecommendation,
        clientRequestedItems: section.clientRequestedItems?.map((item) => ({
            label: item.label,
            detail: item.detail,
        })),
        blockedItems: section.blockedItems?.map((item) => ({
            label: item.label,
            detail: item.detail,
        })),
        handoffPlaceholder: section.handoffPlaceholder
            ? {
                summary: section.handoffPlaceholder.summary,
                statusLabel: "Coming soon",
            }
            : undefined,
        whatWeDoNotPromise: [...section.whatWeDoNotPromise],
        generatedAt: section.generatedAt,
        updatedAt: section.updatedAt,
    };
}
function sanitizeAfterFixSection(section) {
    return {
        schemaId: "projectRecordCenter.sanitizedAfterFix.v1",
        headline: section.headline,
        summaryCounts: { ...section.summaryCounts },
        items: section.items.map((item) => ({
            label: item.label,
            status: item.status,
            summary: item.summary,
        })),
        whatWeDoNotPromise: [...section.whatWeDoNotPromise],
        comparedAt: section.comparedAt ?? section.generatedAt,
        generatedAt: section.generatedAt,
    };
}
function sanitizeOwnershipSection(section) {
    return {
        schemaId: "projectRecordCenter.sanitizedOwnership.v1",
        headline: section.headline,
        subcopy: section.subcopy,
        checklist: section.checklist.map((item) => ({
            label: item.label,
            stateLabel: item.stateLabel,
            note: item.note,
        })),
        riskScoreLabel: section.riskScoreLabel,
        riskScoreExplanation: section.riskScoreExplanation,
        disclaimers: [...section.disclaimers],
        updatedAt: section.updatedAt,
    };
}
function sanitizeDocuments(documents) {
    return documents.map((doc) => ({
        schemaId: "projectRecordCenter.document.v1",
        docType: doc.docType,
        label: doc.label,
        lifecycleStatusLabel: documentLifecycleLabel(doc.lifecycleStatus),
        clientSafeRef: doc.clientSafeRef,
        available: doc.available,
    }));
}
function sanitizeHandoff(handoff) {
    if (!handoff)
        return undefined;
    return {
        schemaId: "projectRecordCenter.handoffSummary.v1",
        ownershipStatus: handoff.ownershipStatus,
        clientSummary: handoff.clientSummary,
        handoffAvailable: handoff.handoffAvailable,
    };
}
function sanitizeWarnings(warnings) {
    if (!warnings?.length)
        return undefined;
    return warnings.map((warning) => ({
        schemaId: "projectRecordCenter.warning.v1",
        code: warning.code,
        message: warning.message,
        severity: warning.severity,
    }));
}
export function sanitizeProjectRecordCenterClientView(view) {
    const sections = view.sections;
    const sanitizedSections = sections
        ? {
            schemaId: "projectRecordCenter.sanitizedClientSections.v1",
            readiness: sections.readiness ? sanitizeReadinessSection(sections.readiness) : undefined,
            fixKit: sections.fixKit ? sanitizeFixKitSection(sections.fixKit) : undefined,
            salesRoom: sections.salesRoom ? sanitizeSalesRoomSection(sections.salesRoom) : undefined,
            afterFix: sections.afterFix ? sanitizeAfterFixSection(sections.afterFix) : undefined,
            ownership: sections.ownership ? sanitizeOwnershipSection(sections.ownership) : undefined,
        }
        : undefined;
    const sanitized = SanitizedProjectRecordCenterClientViewV1Schema.parse({
        schemaId: "projectRecordCenter.sanitizedClientView.v1",
        viewVersion: view.viewVersion,
        summary: {
            schemaId: "projectRecordCenter.sanitizedClientSummary.v1",
            projectModeLabel: projectModeLabel(view.summary.projectMode),
            currentStatusLabel: clientStatusLabel(view.summary.currentStatus),
            nextAction: view.summary.nextAction,
            clientSafe: true,
            generatedAt: view.summary.generatedAt,
        },
        timeline: view.timeline.map(sanitizeTimelineEntry),
        documents: sanitizeDocuments(view.documents),
        handoff: sanitizeHandoff(view.handoff),
        warnings: sanitizeWarnings(view.warnings),
        sections: sanitizedSections,
    });
    assertProjectRecordCenterClientSafe(sanitized);
    return sanitized;
}
export function projectRecordCenterClientLeakCheck(value) {
    const leaks = [];
    const blob = typeof value === "string" ? value : JSON.stringify(value);
    for (const key of PROJECT_RECORD_CENTER_FORBIDDEN_JSON_KEYS) {
        if (blob.includes(`"${key}"`)) {
            leaks.push(key);
        }
    }
    for (const pattern of PROJECT_RECORD_CENTER_FORBIDDEN_PATTERNS) {
        if (pattern.test(blob)) {
            leaks.push(pattern.source);
        }
    }
    const lower = blob.toLowerCase();
    for (const term of FORBIDDEN_INTERNAL_ID_TERMS) {
        if (lower.includes(term.toLowerCase())) {
            leaks.push(`registry:${term}`);
        }
    }
    for (const term of CLIENT_PORTAL_FORBIDDEN_COPY_TERMS) {
        if (term.length < 3)
            continue;
        if (lower.includes(term.toLowerCase())) {
            leaks.push(`registry:${term}`);
        }
    }
    return [...new Set(leaks)];
}
export function assertProjectRecordCenterClientSafe(view) {
    const blob = JSON.stringify(view);
    const leaks = projectRecordCenterClientLeakCheck(blob);
    if (leaks.length > 0) {
        throw new Error(`project_record_center_client_leak:${leaks.join(",")}`);
    }
    assertNoForbiddenClientSafePhrases(blob);
}
//# sourceMappingURL=sanitize-project-record-center-view.js.map